import i18n from "@sg/i18n";
import { z } from "zod";
import { AddressSchema } from "./shared";

enum ControllingPerson {
  UBO = "UBO",
  CONTROLLING = "CONTROLLING",
}

enum OnboardingClassification {
  USER_ID = "user_id",
  ARTICLES_OF_INCORPORATION = "articles_of_incorporation",
  ORG_CHART = "org_chart",
  OTHER = "other",
}

enum OnboardingStatus {
  NOT_STARTED = "not_started",
  IN_PROGRESS = "in_progress",
  IN_REVIEW = "in_review",
  ACCEPTED = "accepted",
  FOLLOW_UP = "follow_up",
  REJECTED = "rejected",
}

enum OnboardingStepType {
  GENERAL = "general_info",
  OTHER = "other_docs",
  UBO = "ubo",
  SUBMIT = "submit",
}

enum OnboardingMode {
  REVIEW = "review",
}

enum PersonalDocumentType {
  DRIVERS_LICENSE = "drivers_license",
  PASSPORT = "passport",
}

const normalizeLeiInput = (u: unknown) => {
  if (u === undefined || u === null) return "";
  if (typeof u === "string") return u.trim().toUpperCase();
  return "";
};

const isValidLeiFormat = (lei: string) => lei.length === 20 && /^[a-z0-9]+$/i.test(lei);

const OnboardingGeneralInformationSchema = z.object({
  lei: z.preprocess(
    normalizeLeiInput,
    z
      .string()
      .min(1, i18n.t("onboarding:lei_required"))
      .length(20, i18n.t("onboarding:lei_length"))
      .regex(/^[a-z0-9]+$/i, i18n.t("onboarding:lei_alphanumeric")),
  ),
  entity_legal_name: z.string(),
  ein: z.string(),
  registered_address: AddressSchema,
  operating_address: AddressSchema,
  operating_geographies: AddressSchema,
  website: z.string().url(),
  nature_of_business: z.string(),
  registered_agencies: z.string(),
  is_ecp: z.boolean().optional(),
  // TODO: Can we make this required if is_ecp is true?
  ecp: z.string().optional(),
});

const trimString = (u: unknown) => (typeof u === "string" ? u.trim() : u);

const OnboardingControllingPersonSchema = z
  .object({
    ubo: z.boolean().optional(),
    controlling_person: z.boolean().optional(),
    first_name: z.preprocess(trimString, z.string().min(1)),
    last_name: z.preprocess(trimString, z.string().min(1)),
    salutation: z.string().optional(),
    middle_name: z.string().optional(),
    address: AddressSchema,
    birthday: z.string().min(1),
    suffix: z.string().optional(),
    title: z.preprocess(trimString, z.string().min(1)),
    email: z.preprocess(trimString, z.string().email().min(1)),
    taxId: z.preprocess(trimString, z.string().min(1)),
    ownership_percentage: z.union([z.number().gte(0).lte(100).default(0), z.nan()]).optional(),
    document_type: z.nativeEnum(PersonalDocumentType),
    document: z.string(),
    document_back: z.string().optional(),
    id_issuing_country: z.preprocess(trimString, z.string().min(1)),
  })
  .superRefine((data, ctx) => {
    if (data.ubo && !data.ownership_percentage) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ownership_percentage"],
        message: i18n.t("required"),
      });
    }
  });

type OnboardingGeneralInformationType = z.infer<typeof OnboardingGeneralInformationSchema>;
type OnboardingControllingPersonType = z.infer<typeof OnboardingControllingPersonSchema>;

export {
  ControllingPerson,
  isValidLeiFormat,
  normalizeLeiInput,
  OnboardingClassification,
  OnboardingControllingPersonSchema,
  OnboardingGeneralInformationSchema,
  OnboardingMode,
  OnboardingStatus,
  OnboardingStepType,
  PersonalDocumentType,
};
export type { OnboardingControllingPersonType, OnboardingGeneralInformationType };
